import { useForm } from "react-hook-form";
import { Brain, Building2, Settings, Target } from "lucide-react";
import Button from "../../components/ui/Button";
import { fieldErrorClassName, inputClassName, labelClassName, selectClassName, textareaClassName } from "./signupShared";
import type { AuthUserAiPreferences } from "../../types/auth";

interface Props {
  defaultValues?: AuthUserAiPreferences;
  onComplete: () => Promise<void>;
}

interface FormValues {
  businessType: string;
  businessName: string;
  roleTitle: string;
  industry: string;
  reportingFrequency: NonNullable<AuthUserAiPreferences["reportingFrequency"]>;
  toneLanguage: NonNullable<AuthUserAiPreferences["toneLanguage"]>;
  responseStyle: NonNullable<AuthUserAiPreferences["responseStyle"]>;
  dataPresentation: NonNullable<AuthUserAiPreferences["dataPresentation"]>;
  recommendationStyle: NonNullable<AuthUserAiPreferences["recommendationStyle"]>;
  decisionSupportStyle: NonNullable<AuthUserAiPreferences["decisionSupportStyle"]>;
  aiPersonality: NonNullable<AuthUserAiPreferences["aiPersonality"]>;
  analysisPreferences: string;
  customInstructions: string;
}

const SectionHeader = ({ Icon, label, color }: { Icon: typeof Brain; label: string; color: string }) => (
  <h3 className={`flex items-center gap-2 text-base font-semibold ${color}`}>
    <Icon className="h-5 w-5" />
    {label}
  </h3>
);

const submitTo = async (path: string, payload: unknown): Promise<{ ok: boolean; error?: string }> => {
  const response = await fetch(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const result = await response.json().catch(() => null) as { error?: string } | null;
    return { ok: false, error: result?.error ?? "Unable to save preferences." };
  }

  return { ok: true };
};

export default ({ defaultValues, onComplete }: Props) => {
  const { register, handleSubmit, setError, formState: { errors, isSubmitting } } = useForm<FormValues>({
    defaultValues: {
      businessType: defaultValues?.businessType ?? "",
      businessName: defaultValues?.businessName ?? "",
      roleTitle: defaultValues?.roleTitle ?? "",
      industry: defaultValues?.industry ?? "",
      reportingFrequency: defaultValues?.reportingFrequency ?? "weekly",
      toneLanguage: defaultValues?.toneLanguage ?? "plain",
      responseStyle: defaultValues?.responseStyle ?? "short",
      dataPresentation: defaultValues?.dataPresentation ?? "text_and_charts",
      recommendationStyle: defaultValues?.recommendationStyle ?? "balanced",
      decisionSupportStyle: defaultValues?.decisionSupportStyle ?? "tell_me",
      aiPersonality: defaultValues?.aiPersonality ?? "coach",
      analysisPreferences: defaultValues?.analysisPreferences ?? "",
      customInstructions: defaultValues?.customInstructions ?? "",
    },
  });

  const onSubmit = handleSubmit(async values => {
    const result = await submitTo("/api/onboarding/preferences", { ...values, complete: true });
    if (!result.ok) {
      setError("businessType", { type: "server", message: result.error });
      return;
    }
    await onComplete();
  });

  const onSkip = async () => {
    const result = await submitTo("/api/onboarding/complete", {});
    if (!result.ok) {
      setError("businessType", { type: "server", message: result.error });
      return;
    }
    await onComplete();
  };

  return (
    <form className="space-y-8" onSubmit={event => void onSubmit(event)}>
      <section className="space-y-4">
        <SectionHeader Icon={Building2} label="Business Context" color="text-emerald-600" />

        <div className="space-y-2">
          <label className={labelClassName} htmlFor="ai-business-type">Business Type</label>
          <input id="ai-business-type" type="text" placeholder="e.g., Construction Company, Consulting Firm, Technology" className={inputClassName} {...register("businessType")} />
          {errors.businessType && <p className={fieldErrorClassName}>{errors.businessType.message}</p>}
        </div>

        <div className="space-y-2">
          <label className={labelClassName} htmlFor="ai-business-name">Business Name</label>
          <input id="ai-business-name" type="text" placeholder="e.g., ABC Construction Ltd, XYZ Consulting" className={inputClassName} {...register("businessName")} />
        </div>

        <div className="space-y-2">
          <label className={labelClassName} htmlFor="ai-role">Your Role/Title</label>
          <input id="ai-role" type="text" placeholder="e.g., CEO, Financial Manager, Owner, Operations Director" className={inputClassName} {...register("roleTitle")} />
        </div>

        <div className="space-y-2">
          <label className={labelClassName} htmlFor="ai-industry">Industry Context (Optional)</label>
          <select id="ai-industry" className={selectClassName} {...register("industry")}>
            <option value="">Select your industry</option>
            <option value="construction">Construction</option>
            <option value="consulting">Consulting</option>
            <option value="technology">Technology</option>
            <option value="retail">Retail</option>
            <option value="manufacturing">Manufacturing</option>
            <option value="hospitality">Hospitality</option>
            <option value="professional_services">Professional Services</option>
            <option value="other">Other</option>
          </select>
        </div>

        <div className="space-y-2">
          <label className={labelClassName} htmlFor="ai-reporting-frequency">Reporting Frequency</label>
          <select id="ai-reporting-frequency" className={selectClassName} {...register("reportingFrequency")}>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
            <option value="quarterly">Quarterly</option>
          </select>
        </div>
      </section>

      <section className="space-y-4">
        <SectionHeader Icon={Brain} label="Communication Style" color="text-indigo-600" />

        <div className="space-y-2">
          <label className={labelClassName} htmlFor="ai-tone">Tone &amp; Language</label>
          <select id="ai-tone" className={selectClassName} {...register("toneLanguage")}>
            <option value="plain">Plain-English / Simplified</option>
            <option value="professional">Professional</option>
            <option value="technical">Technical</option>
          </select>
        </div>

        <div className="space-y-2">
          <label className={labelClassName} htmlFor="ai-response-style">Response Style</label>
          <select id="ai-response-style" className={selectClassName} {...register("responseStyle")}>
            <option value="short">Short summaries with key actions</option>
            <option value="detailed">Detailed explanations</option>
            <option value="bullet">Bullet-point breakdowns</option>
          </select>
        </div>

        <div className="space-y-2">
          <label className={labelClassName} htmlFor="ai-data">Data Presentation</label>
          <select id="ai-data" className={selectClassName} {...register("dataPresentation")}>
            <option value="text">Text only</option>
            <option value="charts">Charts only</option>
            <option value="text_and_charts">Text + Charts</option>
            <option value="tables">Tables</option>
          </select>
        </div>
      </section>

      <section className="space-y-4">
        <SectionHeader Icon={Target} label="Decision Support" color="text-purple-600" />

        <div className="space-y-2">
          <label className={labelClassName} htmlFor="ai-recommendation">Recommendation Style</label>
          <select id="ai-recommendation" className={selectClassName} {...register("recommendationStyle")}>
            <option value="balanced">Balanced</option>
            <option value="conservative">Conservative</option>
            <option value="aggressive">Aggressive</option>
          </select>
        </div>

        <div className="space-y-2">
          <label className={labelClassName} htmlFor="ai-decision">Decision Support Style</label>
          <select id="ai-decision" className={selectClassName} {...register("decisionSupportStyle")}>
            <option value="tell_me">Tell me what to do</option>
            <option value="options">Give me options</option>
            <option value="ask_me">Ask me questions</option>
          </select>
        </div>

        <div className="space-y-2">
          <label className={labelClassName} htmlFor="ai-personality">AI Personality</label>
          <select id="ai-personality" className={selectClassName} {...register("aiPersonality")}>
            <option value="coach">Coach (motivational, supportive)</option>
            <option value="analyst">Analyst (data-driven, factual)</option>
            <option value="advisor">Advisor (strategic, big-picture)</option>
            <option value="neutral">Neutral</option>
          </select>
        </div>
      </section>

      <section className="space-y-4">
        <SectionHeader Icon={Settings} label="Custom Preferences" color="text-amber-600" />

        <div className="space-y-2">
          <label className={labelClassName} htmlFor="ai-analysis">Analysis Preferences (Optional)</label>
          <textarea id="ai-analysis" rows={3} placeholder="e.g., Focus on trend analysis, compare to industry benchmarks..." className={textareaClassName} {...register("analysisPreferences")} />
        </div>

        <div className="space-y-2">
          <label className={labelClassName} htmlFor="ai-instructions">Custom Instructions (Optional)</label>
          <textarea id="ai-instructions" rows={3} placeholder="Any specific instructions for AI analysis..." className={textareaClassName} {...register("customInstructions")} />
        </div>
      </section>

      <div className="space-y-3">
        <Button type="submit" className="h-12 w-full" disabled={isSubmitting}>
          {isSubmitting ? "Saving..." : "Complete Setup"}
        </Button>
        <Button
          type="button"
          variant="outline"
          className="h-12 w-full border-slate-300 bg-white text-sm font-semibold text-slate-700"
          onClick={() => void onSkip()}
          disabled={isSubmitting}
        >
          Skip for Now
        </Button>
      </div>
    </form>
  );
};
